import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Environment, OrbitControls, Plane, Box } from '@react-three/drei'
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Leva } from 'leva'

import { Pathtracer, usePathtracer } from '@react-three/gpu-pathtracer'

import Controls from './Controls'

const rotateBoxes = window.fxrand() < 0.2
let randomCameraAngle = window.fxrand() * Math.PI * 2
let randomCameraHeight = 14 + 14 * Math.floor(window.fxrand())
const randomRotation = () => (rotateBoxes ? window.fxrand() : 0)

const hexValues = '0123456789ABCDEF'.split('')
const randomHEX = () => {
  let hex = '#'
  for (let h = 0; h < 6; h++) {
    hex += hexValues[Math.floor(window.fxrand() * hexValues.length)]
  }
  return hex
}
const randomHSL = (padH, rangeH, padS, rangeS, padL, rangeL) => {
  return `hsl(${Math.round(padH + window.fxrand() * rangeH)},${Math.round(
    padS + window.fxrand() * rangeS
  )}%,${Math.round(padL + window.fxrand() * rangeL)}%)`
}

const huePad = Math.floor(window.fxrand() * 360)
const baseX = 20
const baseY = 20
const floorMetalness = Math.floor(window.fxrand() * 10) / 10
const floorRoughness = Math.floor(window.fxrand() * 3) / 10

const glassType = Math.floor(window.fxrand() * 3)
const altern = window.fxrand() < 0.5
const glassFat = window.fxrand() < 0.7

const singleColor = window.fxrand() < 0.8
const desaturated = singleColor && window.fxrand() < 0.3
const clear = desaturated && window.fxrand() < 0.3

const floorColor = randomHEX()
const boxesCount = 6 + Math.floor(window.fxrand() * 7)
const backgroundIntensity = Math.floor(8 + window.fxrand() * 8)

const features = {
  floorMetalness: floorMetalness,
  floorRoughness: floorRoughness,
  jazz: rotateBoxes,
  shape: glassType === 0 ? 'long long looong' : glassType === 1 ? 'sleepy' : 'cubicus',
  ratio: glassFat ? 'chonky' : 'slick',
  glass: singleColor ? (desaturated ? (clear ? 'clear' : 'desat') : 'monoc') : 'colos',
  count: boxesCount,
}
console.table(features)
window.$fxhashFeatures = features

function Floor() {
  return (
    <>
      <Plane args={[1800, 1600]} position={[-10, 0, 50]} rotation-x={-Math.PI / 2}>
        <meshPhysicalMaterial metalness={floorMetalness} roughness={floorRoughness} color={floorColor} />
      </Plane>
    </>
  )
}
function Thing({ setEnabled, infoRef }) {
  const { clear, update, renderer } = usePathtracer()
  const gl = useThree((state) => state.gl)
  useEffect(() => {
    window.takeScreenshot = () => {
      const link = document.createElement('a')
      link.setAttribute('download', `glass_is_class_${window.fxhash}.png`)
      link.setAttribute('href', gl.domElement.toDataURL('image/png').replace('image/png', 'image/octet-stream'))
      link.click()
    }
  }, [gl])
  const camera = useThree((state) => state.camera)

  useLayoutEffect(() => update(), [])
  useLayoutEffect(() => {
    camera.lookAt(0, 0, 0)
  }, [camera])
  useFrame(() => {
    if (infoRef.current) {
      infoRef.current.children[0].textContent = `${renderer.__r3fState.frames} frames`
      infoRef.current.children[1].textContent = `${renderer.__r3fState.samples} samples`
    }
  }, [])

  return (
    <>
      <OrbitControls
        onEnd={() => setEnabled(true)}
        onStart={() => setEnabled(false)}
        onChange={() => clear()}
        dampingFactor={1}
        zoomSpeed={1}
      />
      <group>
        {/* <Bounds fit clip observe margin={1.7}>
          <group position={[0.2, 0, 0]}>
            <Model rotation-y={Math.PI} position={[-0.3, 0, 0]} scale={5} />
          </group>
        </Bounds> */}
        <Floor />
      </group>
    </>
  )
}

export default function App() {
  const opts = Controls()

  const cameraPosition = useMemo(() => {
    let radius = 32
    return {
      x: Math.floor(radius * Math.cos(randomCameraAngle)),
      y: Math.floor(radius * Math.sin(randomCameraAngle)),
      h: randomCameraHeight,
    }
  }, [])

  const renderedBoxes = useMemo(
    () => (
      <>
        {new Array(boxesCount).fill(0).map((_, key) => {
          let posX = Math.floor(window.fxrand() * baseX) - baseX / 2
          let posY = Math.floor(window.fxrand() * baseY) - baseY / 2

          let w = glassFat ? 8 : 3,
            h = 6,
            d = glassFat ? 8 : 3
          if (glassType === 0) {
            w = glassFat ? 4 : 2
            h = 16
            d = glassFat ? 4 : 2
          } else if (glassType === 1) {
            h = 4
            w = 16
            d = glassFat ? 4 : 2
            if (altern && key % 2 === 0) {
              w = glassFat ? 4 : 2
              d = 16
            }
          }

          let width = Math.floor(1 + window.fxrand() * w)
          let height = Math.floor(1 + window.fxrand() * h)
          let depth = Math.floor(1 + window.fxrand() * d)
          let color = randomHSL(huePad, singleColor ? 10 : 360, desaturated ? 0 : 60, 0, clear ? 100 : 30, 70)
          return (
            <Box
              key={key}
              args={[width, height, depth]}
              position={[posX, height / 2, posY]}
              rotation={[
                Math.PI * 2 * randomRotation(),
                Math.PI * 2 * randomRotation(),
                Math.PI * 2 * randomRotation(),
              ]}
              castShadow
              receiveShadow
              dispose={null}
            >
              <meshPhysicalMaterial
                clearcoat={1}
                clearcoatRoughness={0}
                color={color}
                transmission={1}
                thickness={0.5}
                roughness={0}
              />
            </Box>
          )
        })}
      </>
    ),
    []
  )

  const [enabled, setEnabled] = useState(true)
  const [hiddenControls, setHiddenControls] = useState(true)
  const infoRef = useRef()
  const hideControls = () => {
    setHiddenControls(true)
  }
  const showControls = () => {
    setHiddenControls(false)
  }
  return (
    <>
      <span onMouseLeave={hideControls}>
        <Leva
          // collapsed
          titleBar={{
            title: '',
            filter: false,
          }}
          hidden={hiddenControls}
          flat={true}
        />
      </span>
      <Canvas
        camera={{
          position: [cameraPosition.x, cameraPosition.h, cameraPosition.y],
          fov: 40,
        }}
        gl={{
          preserveDrawingBuffer: true,
        }}
        onMouseEnter={showControls}
      >
        <Suspense fallback={null}>
          <Pathtracer
            // background={{
            //   type: opts.Background_Type,
            //   top: opts.Gradient_ColorTop,
            //   bottom: opts.Gradient_ColorBottom,
            //   intensity: opts.Environment_Intensity,
            //   blur: false,
            // }}
            background={{
              type: 'Gradient',
              top: '#000',
              bottom: '#000',
              intensity: backgroundIntensity,
            }}
            bounces={opts.Rendering_Bounces}
            enabled={enabled}
            // paused={opts.Rendering_Paused}
            samples={opts.Rendering_Samples}
            resolutionScale={opts.Rendering_Scale}
          >
            <Environment preset="night" background={false} />
            <Thing setEnabled={setEnabled} infoRef={infoRef} />
            {/* <Box args={[2, 22, 2]} position={[2, 4, 2]} castShadow receiveShadow dispose={null}>
              <meshPhysicalMaterial
                clearcoat={1}
                clearcoatRoughness={0}
                color="red"
                transmission={1}
                thickness={0.5}
                roughness={0}
              />
            </Box>
            <Box args={[5, 25, 5]} position={[5, 4, 5]} castShadow receiveShadow dispose={null}>
              <meshPhysicalMaterial
                clearcoat={1}
                clearcoatRoughness={0}
                color="white"
                transmission={1}
                thickness={0.5}
                roughness={0}
              />
            </Box> */}

            {renderedBoxes}
          </Pathtracer>
        </Suspense>
        {/* <Stats /> */}
      </Canvas>
      <div className="info" ref={infoRef}>
        <p>0 frames</p>
        <p>0 samples</p>
      </div>
    </>
  )
}
